import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { ChevronDown, Check } from "lucide-react";

let __customSelectIdCounter = 0;
function useStableId(prefix = "cs") {
  const ref = useRef(null);
  if (ref.current === null) {
    __customSelectIdCounter += 1;
    ref.current = `${prefix}-${__customSelectIdCounter}`;
  }
  return ref.current;
}

/**
 * Fully custom select dropdown built with styled-components.
 * Designed to visually match existing metadata-field styling.
 *
 * Props:
 * - label: string
 * - options: Array<{ value: string, label: string, disabled?: boolean }>
 * - value: string
 * - onChange: (nextValue: string) => void
 * - placeholderLabel?: string (preview text when no value selected, not in list)
 */
export default function CustomSelect({
  label,
  options,
  value,
  onChange,
  placeholderLabel,
  disabled,
}) {
  const listboxId = useStableId("custom-select-listbox");
  const triggerId = useStableId("custom-select-trigger");
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const optionsRef = useRef([]);

  const safeOptions = Array.isArray(options) ? options : [];
  const selected = useMemo(
    () => safeOptions.find((o) => o.value === value),
    [safeOptions, value],
  );

  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 0 });
  const [direction, setDirection] = useState("down"); // "down" | "up"
  const [activeIndex, setActiveIndex] = useState(() => {
    const idx = safeOptions.findIndex((o) => o.value === value);
    return idx >= 0 ? idx : 0;
  });

  useEffect(() => {
    const idx = safeOptions.findIndex((o) => o.value === value);
    if (idx >= 0) setActiveIndex(idx);
  }, [value, safeOptions]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      const root = rootRef.current;
      const panel = panelRef.current;
      if (!root) return;
      if (
        root.contains(e.target) ||
        (panel && panel.contains && panel.contains(e.target))
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const setValue = (nextValue) => {
    if (onChange) onChange(nextValue);
    setOpen(false);
  };

  const updatePanelPosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldOpenUp = spaceBelow < 260 && spaceAbove > spaceBelow;

    // Measure rendered height when possible (respects max-height: 260px).
    const panelHeight =
      panelRef.current?.getBoundingClientRect?.().height ||
      Math.min(260, shouldOpenUp ? Math.max(0, spaceAbove - 8) : 260);

    const nextTop = shouldOpenUp
      ? Math.max(8, rect.top - panelHeight - 8)
      : Math.min(rect.bottom + 8, window.innerHeight - panelHeight - 8);

    setDirection(shouldOpenUp ? "up" : "down");
    setPanelPos({ top: nextTop, left: rect.left, width: rect.width });
  };

  // Mount/unmount for close animation
  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    if (!mounted) return;
    const t = setTimeout(() => setMounted(false), 150);
    return () => clearTimeout(t);
  }, [open, mounted]);

  // Positioning: on open + on scroll/resize
  useEffect(() => {
    if (!open) return;
    // Initial position (may refine after portal mounts/measures)
    updatePanelPosition();

    const onScrollOrResize = () => updatePanelPosition();
    // capture scroll events from nested scroll containers too
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);

    const raf = window.requestAnimationFrame(() => {
      // Recalculate once the panel is mounted so we can measure height,
      // and focus the listbox for keyboard navigation.
      updatePanelPosition();
      if (panelRef.current && typeof panelRef.current.focus === "function") {
        panelRef.current.focus();
      }
    });

    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      window.cancelAnimationFrame(raf);
    };
  }, [open]);

  const moveActive = (delta) => {
    if (!safeOptions.length) return;
    let next = activeIndex;
    for (let i = 0; i < safeOptions.length; i += 1) {
      next = (next + delta + safeOptions.length) % safeOptions.length;
      if (!safeOptions[next]?.disabled) break;
    }
    setActiveIndex(next);
    const el = optionsRef.current[next];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  };

  const onTriggerKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      moveActive(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) setOpen(true);
      moveActive(-1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      } else {
        const opt = safeOptions[activeIndex];
        if (opt && !opt.disabled) setValue(opt.value);
      }
    }
  };

  const onListboxKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveActive(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = safeOptions[activeIndex];
      if (opt && !opt.disabled) setValue(opt.value);
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  const displayLabel =
    selected?.label ||
    (placeholderLabel && !value ? placeholderLabel : "") ||
    "";
  const isPlaceholder = !value;

  return (
    <FieldBox
      ref={rootRef}
      className="metadata-field-box"
      data-open={open ? "true" : "false"}
    >
      <Label className="label label-uppercase metadata-field-label">
        {label}
      </Label>

      <Trigger
        id={triggerId}
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={(e) => !disabled && onTriggerKeyDown(e)}
        $open={open}
        $placeholder={isPlaceholder}
        $disabled={disabled}
      >
        <ValueText>{displayLabel}</ValueText>
        <ArrowWrap $open={open} aria-hidden="true">
          <ChevronDown size={16} />
        </ArrowWrap>
      </Trigger>

      {mounted &&
        createPortal(
          <Panel
            ref={panelRef}
            role="listbox"
            id={listboxId}
            tabIndex={open ? 0 : -1}
            aria-labelledby={triggerId}
            $open={open}
            $direction={direction}
            onKeyDown={onListboxKeyDown}
            style={{
              top: `${panelPos.top}px`,
              left: `${panelPos.left}px`,
              width: `${panelPos.width}px`,
            }}
          >
            {safeOptions.map((opt, idx) => {
              const isSelected = opt.value === value && value !== "";
              const isActive = idx === activeIndex;
              return (
                <OptionRow
                  key={`${opt.value}-${idx}`}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={!!opt.disabled}
                  ref={(el) => {
                    optionsRef.current[idx] = el;
                  }}
                  tabIndex={-1}
                  $selected={isSelected}
                  $active={isActive}
                  $disabled={!!opt.disabled}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onMouseDown={(e) => e.preventDefault()} // keep focus on listbox
                  onClick={() => {
                    if (!opt.disabled) setValue(opt.value);
                  }}
                >
                  <span>{opt.label}</span>
                  {isSelected && (
                    <CheckWrap aria-hidden="true">
                      <Check size={16} />
                    </CheckWrap>
                  )}
                </OptionRow>
              );
            })}
          </Panel>,
          document.body,
        )}
    </FieldBox>
  );
}

const FieldBox = styled.div`
  position: relative;
`;

const Label = styled.h1``;

// Trigger matches the current metadata select rhythm (height/padding/typography)
const Trigger = styled.button`
  width: 100%;
  height: 40px;
  min-height: 40px;
  border: none;
  border-radius: 0;
  background-color: var(--color-tertiary);
  padding: 8px 14px 6px 14px;
  padding-right: 38px;

  font-family: "Roboto", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${(p) =>
    p.$placeholder ? "var(--color-text-secondary)" : "var(--color-text)"};

  outline: none;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  transition: background-color 140ms ease, box-shadow 140ms ease;

  &:hover {
    background-color: color-mix(
      in srgb,
      var(--color-tertiary) 88%,
      var(--color-surface)
    );
  }

  ${(p) =>
    p.$open
      ? `
    border-radius: 16px;
  `
      : ""}
`;

const ValueText = styled.span`
  flex: 1;
  text-align: left;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ArrowWrap = styled.span`
  position: absolute;
  right: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);
  transition: transform 140ms ease;
  transform: rotate(${(p) => (p.$open ? "180deg" : "0deg")});
`;

const Panel = styled.div`
  position: fixed;
  z-index: 2000;

  background: var(--color-surface);
  border: 1px solid
    color-mix(in srgb, var(--color-border) 55%, var(--color-surface));
  border-radius: 20px;
  box-shadow: 0 10px 26px var(--color-shadow);

  padding: 8px;
  max-height: 220px;
  overflow: auto;
  outline: none;

  opacity: ${(p) => (p.$open ? 1 : 0)};
  transform: translateY(${(p) =>
    p.$open ? "0" : p.$direction === "up" ? "4px" : "-4px"});
  pointer-events: ${(p) => (p.$open ? "auto" : "none")};
  transition: opacity 140ms ease-out, transform 140ms ease-out;
`;

const OptionRow = styled.div`
  height: 38px;
  padding: 0 14px;
  border-radius: 14px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  font-family: "Roboto", sans-serif;
  font-size: 16px;
  font-weight: ${(p) => (p.$selected ? 600 : 500)};
  color: ${(p) =>
    p.$selected ? "var(--color-primary)" : "var(--color-text)"};

  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};

  background: ${(p) =>
    p.$selected
      ? "color-mix(in srgb, var(--color-primary) 12%, var(--color-surface))"
      : p.$active
        ? "color-mix(in srgb, var(--color-border) 18%, var(--color-surface))"
        : "transparent"};

  transition: background-color 140ms ease;

  &:hover {
    background: ${(p) =>
      p.$disabled
        ? "transparent"
        : "color-mix(in srgb, var(--color-border) 18%, var(--color-surface))"};
  }
`;

const CheckWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);
`;

